import { ADMINISTRATIE_XEBIA_BELGIUM, ADMINISTRATIE_XEBIA_CLOUD_CONTROL } from './administraties';

/**
 * EU Member States ISO 3166-1 alpha-2 country codes.
 */
export const EU_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
]);

/**
 * Determines VAT rate, code, and percentage based on customer administratie,
 * country, and computed vatCode.
 *
 * Rules:
 * - Xebia Cloud Control B.V.: Always 0% VAT.
 * - Xebia Belgium B.V.:
 *     BE -> V03 (21%)
 *     Non-BE (EU / non-EU) -> V07 (0%)
 * - Xebia Workplace B.V. / Nederland B.V. / Default:
 *     NL -> 1a (21%)
 *     EU non-NL -> 3bS (0%, reverse charge)
 *     Non-EU -> 3aS (0%, export)
 *
 * @param {Object} customer - Customer object with administratie, country, and/or vatCode
 * @returns {{ vatRate: number, vatCode: string, vatPercent: number }}
 */
export const getVatDetails = (customer) => {
  const administratie = customer?.administratie;

  if (administratie === ADMINISTRATIE_XEBIA_CLOUD_CONTROL) {
    return {
      vatRate: 0.0,
      vatCode: '0%',
      vatPercent: 0,
    };
  }

  // If customer already has a computed vatCode from backend
  const vatCode = customer?.vatCode;
  if (vatCode && typeof vatCode === 'string' && !vatCode.startsWith('Error')) {
    if (['1a', 'V03'].includes(vatCode)) {
      return {
        vatRate: 0.21,
        vatCode,
        vatPercent: 21,
      };
    }
    if (['3bS', '3aS', 'V07'].includes(vatCode)) {
      return {
        vatRate: 0.0,
        vatCode,
        vatPercent: 0,
      };
    }
  }

  // Fallback: derive from administratie and country
  const country = customer?.country ? String(customer.country).trim().toUpperCase() : '';

  if (administratie === ADMINISTRATIE_XEBIA_BELGIUM) {
    if (country === 'BE') {
      return { vatRate: 0.21, vatCode: 'V03', vatPercent: 21 };
    }
    return { vatRate: 0.0, vatCode: 'V07', vatPercent: 0 };
  }

  // Default to Workplace / Nederland / Dutch entities
  if (country === 'NL') {
    return { vatRate: 0.21, vatCode: '1a', vatPercent: 21 };
  }
  if (country && EU_COUNTRIES.has(country)) {
    return { vatRate: 0.0, vatCode: '3bS', vatPercent: 0 };
  }
  if (country) {
    return { vatRate: 0.0, vatCode: '3aS', vatPercent: 0 };
  }

  // Fallback if no country specified: domestic 21%
  return { vatRate: 0.21, vatCode: '1a', vatPercent: 21 };
};
